import { Children, type FC, type ReactNode } from "react";
import { Platform, View, type DimensionValue, type PointerEvent, type StyleProp, type ViewStyle } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import { Text, type TextSize } from "../Text";
import {
  useTableContext,
  useTableLayout,
  useTableSection,
  type TableCellVariant,
  type TableSize,
} from "./TableContext";

export interface TableCellProps {
  /**
   * Cell content. Text (strings and numbers, including mixed ones such as
   * `{count} vehicles`) is rendered in the section's text style.
   */
  readonly children?: ReactNode;
  /** Horizontal alignment of the content. Defaults to `"left"`. */
  readonly align?: "left" | "center" | "right" | undefined;
  /**
   * Fixed column width. Give every cell in a column the same `width` (or the
   * same `flex`) so the column lines up across rows.
   */
  readonly width?: DimensionValue | undefined;
  /** Share of the remaining row width when no `width` is set. Defaults to 1. */
  readonly flex?: number | undefined;
  /** Smallest width the column may shrink to. */
  readonly minWidth?: DimensionValue | undefined;
  /** `"none"` removes the cell padding, for content that draws its own. Defaults to `"normal"`. */
  readonly padding?: "normal" | "none" | undefined;
  /** Overrides the variant inferred from the enclosing `TableHead` / `TableBody` / `TableFooter`. */
  readonly variant?: TableCellVariant | undefined;
  /** Overrides the size set on `Table`. */
  readonly size?: TableSize | undefined;
  /**
   * Keep text content to one line, truncated with an ellipsis. Shorthand for
   * `lineClamp={1}`. Text wraps by default.
   */
  readonly noWrap?: boolean | undefined;
  /**
   * Let text wrap up to this many lines, then truncate with an ellipsis.
   * On web, hovering truncated text shows the full text as a tooltip.
   */
  readonly lineClamp?: number | undefined;
  readonly style?: StyleProp<ViewStyle> | undefined;
  readonly testID?: string | undefined;
}

const TEXT_STYLE: Readonly<Record<TableCellVariant, TextSize>> = {
  head: "xs/regular",
  body: "sm/regular",
  footer: "sm/regular",
};

const ALIGN_ITEMS = { left: "flex-start", center: "center", right: "flex-end" } as const;

/**
 * A table cell, modelled on MUI's `TableCell`. Renders as a column header inside `TableHead`.
 *
 * In the stacked layout a cell spans the row and ignores `width`, `flex`,
 * `minWidth`, `padding` and `align`. An empty cell, such as a spacer column,
 * isn't rendered there.
 */
const TableCell: FC<TableCellProps> = ({
  children,
  align = "left",
  width,
  flex = 1,
  minWidth,
  padding = "normal",
  variant,
  size,
  noWrap = false,
  lineClamp,
  style,
  testID,
}) => {
  const table = useTableContext();
  const section = useTableSection();
  const stacked = useTableLayout() === "stacked";
  const resolvedVariant = variant ?? section;
  const resolvedSize = size ?? table.size;
  const lines = lineClamp ?? (noWrap ? 1 : undefined);
  const text = isText(children);

  if (stacked && Children.toArray(children).length === 0) return null;

  const resolvedAlign = stacked ? "left" : align;

  const content =
    text ?
      <Text
        textStyle={TEXT_STYLE[resolvedVariant]}
        color={resolvedVariant === "body" ? "text" : "textMuted"}
        textAlign={resolvedAlign}
        numberOfLines={lines}
        style={styles.text}
      >
        {children}
      </Text>
    : children;

  return (
    <View
      role={resolvedVariant === "head" ? "columnheader" : "cell"}
      testID={testID}
      onPointerEnter={text && lines !== undefined && Platform.OS === "web" ? showFullTextIfTruncated : undefined}
      style={[
        styles.cell,
        { alignItems: ALIGN_ITEMS[resolvedAlign] },
        stacked ? styles.stacked
        : width === undefined ? { flex, flexBasis: 0 }
        : { width, flexGrow: 0, flexShrink: 0 },
        !stacked && minWidth !== undefined && { minWidth },
        !stacked && padding === "normal" && (resolvedSize === "small" ? styles.smallPadding : styles.mediumPadding),
        style,
      ]}
    >
      {content}
    </View>
  );
};

TableCell.displayName = "TableCell";

// `{count} vehicles` arrives as an array of a number and a string. Bare text
// must sit inside <Text> on native, so any all-text content is wrapped.
function isText(children: ReactNode): boolean {
  const nodes = Children.toArray(children);
  return nodes.length > 0 && nodes.every((node) => typeof node === "string" || typeof node === "number");
}

// Web only: RN Text drops `title`, so set it on the cell's element while the
// pointer is over it, and only when the text is actually clipped. Checked on
// each hover because column widths change with the viewport.
function showFullTextIfTruncated(event: PointerEvent): void {
  const cell: unknown = event.currentTarget;
  if (typeof HTMLElement === "undefined" || !(cell instanceof HTMLElement)) return;
  const textElement = cell.firstElementChild;
  if (!(textElement instanceof HTMLElement)) return;

  // One clamped line overflows sideways, several overflow downwards. The 1px
  // allowance absorbs rounding, and a zero height means no measurement yet.
  const { scrollWidth, clientWidth, scrollHeight, clientHeight } = textElement;
  const truncated = scrollWidth > clientWidth + 1 || (clientHeight > 0 && scrollHeight > clientHeight + 1);
  if (truncated && textElement.textContent) cell.title = textElement.textContent;
  else cell.removeAttribute("title");
}

export { TableCell };

const styles = StyleSheet.create((theme) => ({
  cell: {
    justifyContent: "center",
    // Lets flex columns shrink below their content so long text truncates
    // instead of pushing the row wider than the table.
    minWidth: 0,
  },
  text: {
    alignSelf: "stretch",
  },
  stacked: {
    alignSelf: "stretch",
  },
  mediumPadding: {
    paddingHorizontal: theme.spacing["3xl"],
    paddingVertical: theme.spacing.xl,
  },
  smallPadding: {
    paddingHorizontal: theme.spacing["3xl"],
    paddingVertical: theme.spacing.sm,
  },
}));
